import {
  DateRangeOptions,
  localDateRange,
} from "@faire/web--source/common/localization/localDateRange";
import { IShipWindow } from "@faire/web-api--source/indigofair/data/IShipWindow";
import { addDays } from "date-fns/addDays";
import { differenceInDays } from "date-fns/differenceInDays";
import { FormatDateOptions } from "react-intl";

import { DEFAULT_LOCALE } from "./consts/DEFAULT_LOCALE";
import { FaireDate } from "./FaireDate";
import {
  AllowedFormats,
  intlDate,
  intlDateFormatOptions,
} from "./localization/date-fns/intlDate";
import { LocalizationStore } from "./localization/LocalizationStore";
import { MutableFaireDateRange } from "./MutableFaireDateRange";
import { isNotUndefined } from "./typescriptUtils";
import { Locale } from "date-fns";

/** Faire abstraction of date-without-time range. */
interface IFaireDateRangeFormat {
  /**
   * The date format to apply to both to start,
   * end and single date, unless overridden with other fields.
   */
  dateFormat?: AllowedFormats;
  /**
   * The date format to apply to start and end date (but not single date).
   */
  multiDayFormat?: AllowedFormats;
  /** Allows to set some common predefined formats. */
  presetDateFormat?: PresetDateFormat | string;
}

/**
 * @deprecated use localFormat
 */
export enum PresetDateFormat {
  /**
   * single: MMM d
   * within month: MMM d–d
   * across month: MMM d–MMM d
   * across year: MMM d, yyyy–MMM d, yyyy
   */
  ABBREVIATED_MMM_D,

  /**
   * single: MMM d, yyyy
   * within month: MMM d–d, yyyy
   * across month: MMM d–MMM d, yyyy
   * across year: MMM d, yyyy–MMM d, yyyy
   */
  ABBREVIATED_MMM_D_YYYY,

  /**
   * single: MMM yyyy
   * within month: MMM yyyy
   * across month: MMM–MMM yyyy
   * across year: MMM yyyy–MMM yyyy
   */
  ABBREVIATED_MMM_YYYY,

  /**
   * single: MMMM d
   * within month: MMMM d–d
   * across month: MMMM d–MMMM d
   * across year: MMMM d, yyyy–MMMM d, yyyy
   */
  MMMM_D,

  /**
   * single: MMMM d, yyyy
   * within month: MMMM d–d, yyyy
   * across month: MMMM d–MMMM d, yyyy
   * across year: MMMM d, yyyy–MMMM d, yyyy
   */
  MMMM_D_YYYY,
}

const PRESET_DATE_FORMATS = {
  [PresetDateFormat.ABBREVIATED_MMM_D]: {
    month: "short",
    day: "numeric",
  },
  [PresetDateFormat.ABBREVIATED_MMM_YYYY]: {
    year: "numeric",
    month: "short",
  },
  [PresetDateFormat.ABBREVIATED_MMM_D_YYYY]: {
    year: "numeric",
    month: "short",
    day: "numeric",
  },
  [PresetDateFormat.MMMM_D]: {
    month: "long",
    day: "numeric",
  },
  [PresetDateFormat.MMMM_D_YYYY]: {
    year: "numeric",
    month: "long",
    day: "numeric",
  },
};

export class FaireDateRange {
  /**
   * Construct date range.
   *
   * @param startDate Start range date.
   * @param endDate End range date. Same as start date is interpreted as a
   *     single date instead of an range.
   */
  constructor(
    public readonly startDate: FaireDate,
    public readonly endDate: FaireDate
  ) {}

  toMutable = (): MutableFaireDateRange =>
    new MutableFaireDateRange(this.startDate, this.endDate);

  get windowDays(): number {
    return differenceInDays(
      this.endDate.localDateObject,
      this.startDate.localDateObject
    );
  }

  /**
   * @deprecated use localFormat
   */
  formatPresetDateRange = (
    presetDateFormat: PresetDateFormat | string
  ): string | undefined => {
    // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
    const opts: FormatDateOptions = PRESET_DATE_FORMATS[presetDateFormat] ?? {
      year: "numeric",
      month: "long",
      day: "numeric",
    };

    const localizeString = LocalizationStore.get().intl.formatDateTimeRange(
      this.startDate.localDateObject,
      this.endDate.localDateObject,
      opts
    );

    return LocalizationStore.get().locale === DEFAULT_LOCALE
      ? localizeString.replace(" – ", "–")
      : localizeString;
  };

  localFormat = (formatters: DateRangeOptions) =>
    localDateRange(
      this.startDate.valueOf(),
      this.endDate.valueOf(),
      formatters
    );

  /**
   * @deprecated use FaireDateRange.localFormat
   * Default format is 'MMMM d' if nothing provided.
   */
  format = (
    faireDateRangeFormat: IFaireDateRangeFormat = {},
    locale?: Locale
  ): string | undefined => {
    if (!this.startDate) {
      return;
    }

    if (isNotUndefined(faireDateRangeFormat.presetDateFormat)) {
      return this.formatPresetDateRange(faireDateRangeFormat.presetDateFormat);
    }

    const { dateFormat = "MMMM d", multiDayFormat = dateFormat } =
      faireDateRangeFormat;

    // single day date range
    if (this.startDate.valueOf() === this.endDate.valueOf()) {
      return `${intlDate(this.startDate.localDateObject, dateFormat, locale)}`;
    }

    const localizeString = LocalizationStore.get().intl.formatDateTimeRange(
      this.startDate.localDateObject,
      this.endDate.localDateObject,
      intlDateFormatOptions(multiDayFormat)
    );

    return LocalizationStore.get().locale === DEFAULT_LOCALE
      ? localizeString.replace(" – ", "–")
      : localizeString;
  };

  toShipWindow = (): IShipWindow => {
    return IShipWindow.build({
      start_date: this.startDate.normalizedUtcTimestamp,
      window_days: this.windowDays,
    });
  };

  static fromShipWindow = (
    shipWindow: IShipWindow | undefined
  ): FaireDateRange | undefined => {
    if (shipWindow === undefined) {
      return undefined;
    }

    return FaireDateRange.create(shipWindow.start_date, shipWindow.window_days);
  };

  static create = (
    startDateMillis: number | undefined,
    days: number = 0
  ): FaireDateRange | undefined => {
    if (startDateMillis == null) {
      return undefined;
    }

    const startDate = new Date(startDateMillis);
    const endDate = addDays(startDate, days);
    return new FaireDateRange(
      FaireDate.fromLocalDateObject(startDate),
      FaireDate.fromLocalDateObject(endDate)
    );
  };

  static isEqual = (
    a: FaireDateRange | undefined,
    b: FaireDateRange | undefined
  ): boolean => {
    return compareFaireDateRanges(a, b) === 0;
  };
}

/**
 * Compares two date ranges.
 *
 * Range A is consider smaller than range B if A start date is
 * smaller than B start date, or, in the case of same start dates, A end date is
 * smaller than B end date. If both start and end date are the same, the
 * A and B are consider equal. Otherwise, B is considered greater than A.
 * Undefined range is treated as the smallest possible value, and hence
 * deemed smaller than all other ranges (two undefined ranges are
 * considered equal.
 *
 * Returns a negative value if first range is less than second range,
 * zero if they're equal and a positive value otherwise.
 */
export const compareFaireDateRanges = (
  a: FaireDateRange | undefined,
  b: FaireDateRange | undefined
): number => {
  const aStartTimestamp = a?.startDate.normalizedUtcTimestamp ?? 0;
  const bStartTimestamp = b?.startDate.normalizedUtcTimestamp ?? 0;
  return aStartTimestamp === bStartTimestamp
    ? (a?.endDate.normalizedUtcTimestamp ?? 0) -
        (b?.endDate.normalizedUtcTimestamp ?? 0)
    : aStartTimestamp - bStartTimestamp;
};
